import type { ReactNode } from 'react'

import {
  TextLoader,
  EmptyState,
  Space,
  StatusBadge,
  Tag,
  TertiaryButton,
  Text,
  WithTooltip,
} from '@procurify/ui'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import messages from './messages'
import { SelectableRowStyled } from './styles/SelectableRowStyled'

export interface IUnmatchedCreditCardItemsPaneProps {
  items: CreditCardItem[]
  isLoading: boolean
  selectedItemIds: number[]
  /** Card currency for the "Invalid currency" badge (BHV-032) */
  cardCurrencyId: Nullable<number>
  /** Click/shift-click selection — range math lives in the presenter (BHV-031) */
  onRowClick: (item: CreditCardItem, index: number, isShiftKey: boolean) => void
  /** Filters the sibling statement pane (BHV-037) */
  onShowSuggestedMatches: (item: CreditCardItem) => void
  /** Slot for inline-edit-credit-card-item (ADR-9) */
  renderInlineCostCell: (item: CreditCardItem) => ReactNode
  /** Slot for the Edit / View audit log / Remove menu (ADR-9) */
  renderRowMenu: (item: CreditCardItem) => ReactNode
}

export const UnmatchedCreditCardItemsPane = ({
  items,
  isLoading,
  selectedItemIds,
  cardCurrencyId,
  onRowClick,
  onShowSuggestedMatches,
  renderInlineCostCell,
  renderRowMenu,
}: IUnmatchedCreditCardItemsPaneProps) => {
  const intl = useProcurifyIntl()

  return (
    <PaginationControls id='ap-reconcile-unmatched-items'>
      <Space>
        <Text bold autoMargin={false}>
          {intl.formatMessage(messages.paneTitle)}
        </Text>

        {/* Per-pane search + sort (BHV-052) */}
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space.Item minWidth={240}>
            <PaginationControls.Search
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
            />
          </Space.Item>
          <Space.Item minWidth={160}>
            <PaginationControls.Sort
              sortKeys={[
                {
                  label: intl.formatMessage(messages.sortDateLabel),
                  value: 'item__received_on',
                },
                {
                  label: intl.formatMessage(messages.sortTotalCostLabel),
                  value: 'total_cost',
                },
                {
                  label: intl.formatMessage(messages.sortDescriptionLabel),
                  value: 'item__description',
                },
              ]}
            />
          </Space.Item>
        </Space>

        {isLoading && <TextLoader />}

        {!isLoading && items.length === 0 && (
          <EmptyState header={intl.formatMessage(messages.emptyStateHeader)} />
        )}

        {!isLoading && items.length > 0 && (
          <div>
            {items.map((item, index) => {
              const isSelected = selectedItemIds.includes(item.id)

              return (
                <SelectableRowStyled
                  key={item.id}
                  $selected={isSelected}
                  onClick={(event) => onRowClick(item, index, event.shiftKey)}
                  role='button'
                  aria-pressed={isSelected}
                  aria-label={intl.formatMessage(messages.selectRowAriaLabel, {
                    description: item.descriptionLabel,
                  })}
                >
                  <Space size='small'>
                    <Space
                      direction='horizontal'
                      justify='space-between'
                      align='center'
                    >
                      <Space direction='horizontal' size='small' align='center'>
                        {item.po && (
                          <>
                            <Tag>{intl.formatMessage(messages.poTag)}</Tag>
                            <Text bold autoMargin={false}>
                              {item.po.number}
                            </Text>
                          </>
                        )}
                        {item.expense && (
                          <>
                            <Tag>{intl.formatMessage(messages.rfeTag)}</Tag>
                            <Text bold autoMargin={false}>
                              {item.expense.expenseReportId}
                            </Text>
                          </>
                        )}
                        <Text autoMargin={false}>{item.descriptionLabel}</Text>
                        {item.suggestedMatchCount > 0 && (
                          <TertiaryButton
                            type='button'
                            onClick={(event) => {
                              event.stopPropagation()
                              onShowSuggestedMatches(item)
                            }}
                          >
                            {intl.formatMessage(messages.suggestedMatchesChip, {
                              count: item.suggestedMatchCount,
                            })}
                          </TertiaryButton>
                        )}
                      </Space>
                      <div onClick={(event) => event.stopPropagation()}>
                        {renderRowMenu(item)}
                      </div>
                    </Space>

                    <Space direction='horizontal' align='center'>
                      {item.packingSlips.length > 0 && (
                        <WithTooltip title={item.packingSlips[0].name}>
                          <WithTooltip.Wrapper>
                            <Text color='textSecondary' autoMargin={false}>
                              {intl.formatMessage(messages.packingSlipLabel)}
                            </Text>
                          </WithTooltip.Wrapper>
                        </WithTooltip>
                      )}
                      <Text autoMargin={false}>
                        <FormattedShortDate date={item.receivedOn} />
                      </Text>
                      <Text color='textSecondary' autoMargin={false}>
                        {item.accountCodeLabel}
                      </Text>
                      <div onClick={(event) => event.stopPropagation()}>
                        {renderInlineCostCell(item)}
                      </div>
                      {cardCurrencyId !== null &&
                        item.hasInvalidCurrency(cardCurrencyId) && (
                          <WithTooltip
                            title={intl.formatMessage(
                              messages.invalidCurrencyTooltip
                            )}
                          >
                            <WithTooltip.Wrapper>
                              <StatusBadge
                                type='error'
                                text={intl.formatMessage(
                                  messages.invalidCurrencyLabel
                                )}
                              />
                            </WithTooltip.Wrapper>
                          </WithTooltip>
                        )}
                    </Space>
                  </Space>
                </SelectableRowStyled>
              )
            })}
          </div>
        )}

        <Space direction='horizontal' justify='end' align='center'>
          <PaginationControls.Page />
        </Space>
      </Space>
    </PaginationControls>
  )
}

UnmatchedCreditCardItemsPane.displayName = 'UnmatchedCreditCardItemsPane'
